import { Suspense, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Box, Typography, keyframes } from "@mui/material";
import Layout from "./components/Layout";
import SidebarLayout from "./components/SidebarLayout";
import ChatLayout from "./components/ChatLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import { routeConfig } from "./config/routes";
import { NotificationProvider } from "./context/NotificationContext";
import { SSEProvider } from "./context/SSEContext";
import { UnityProvider } from "./context/UnityContext";
import { DevLogProvider } from "./context/DevLogContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import {
  INTERPRETER_BASE_URL,
  VISUALIZER_BASE_URL,
  MODE,
} from "./config/runtime";

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const renderRoute = route => (
  <Route key={route.path} path={route.path} element={<route.component />} />
);

const DelayedSuspense = ({ children, fallback, delay = 3000 }) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  if (isLoading) return fallback;

  return <Suspense fallback={fallback}>{children}</Suspense>;
};

const LoadingFallback = () => {
  const { isDark } = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        backgroundColor: isDark ? "#16181A" : "#ECEFF3",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          {[0, 150, 300, 450, 600].map((delay, index) => (
            <Box
              key={index}
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: isDark ? "#286540" : "#146E37",
                animation: `${bounce} 1.4s infinite ease-in-out`,
                animationDelay: `${delay}ms`,
              }}
            />
          ))}
        </Box>
        <Typography
          sx={{
            fontSize: "18px",
            color: isDark ? "#D1D5DB" : "#666666",
            animation: `${pulse} 2s infinite`,
          }}
        >
          Loading...
        </Typography>
      </Box>
    </Box>
  );
};

const AppContent = () => {
  const { isDark } = useTheme();

  return (
    <Box
      sx={{
        height: "100vh",
        overflow: "hidden",
        background: isDark ? "#16181A" : "#ECEFF3",
      }}
    >
      <DelayedSuspense fallback={<LoadingFallback />} delay={3000}>
        <Routes>
          {/* Public routes */}
          {routeConfig.public.map(renderRoute)}

          {/* Sidebar routes — Sidebar + Outlet via SidebarLayout */}
          <Route
            element={
              <ProtectedRoute>
                <SidebarLayout />
              </ProtectedRoute>
            }
          >
            {/* Routes without chat panel */}
            {routeConfig.withSidebar.map(renderRoute)}

            {/* Routes with shared AllFiles chat panel */}
            <Route element={<ChatLayout />}>
              {routeConfig.withSidebarAndChat.map(renderRoute)}
            </Route>
          </Route>

          {/* Protected routes without Sidebar */}
          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            {routeConfig.protected.map(renderRoute)}
          </Route>

          {/* Fallback — redirect unknown paths to home */}
          <Route path='*' element={<Navigate to='/' replace />} />
        </Routes>
      </DelayedSuspense>
    </Box>
  );
};

const App = () => {
  // Log environment config once on mount to aid debugging
  useEffect(() => {
    console.log("🎯 Frontend Application Starting...");
    console.log("   Environment Variables:");
    console.log(
      `   - INTERPRETER_BASE_URL: ${INTERPRETER_BASE_URL || "NOT SET"}`,
    );
    console.log(
      `   - VISUALIZER_BASE_URL: ${VISUALIZER_BASE_URL || "NOT SET"}`,
    );
    console.log(`   - MODE: ${MODE || "NOT SET"}`);
    console.log("✅ Frontend Application Ready");
  }, []);

  return (
    <ThemeProvider>
      <NotificationProvider>
        <SSEProvider>
          <DevLogProvider>
            <BrowserRouter>
              <UnityProvider>
                <AppContent />
              </UnityProvider>
            </BrowserRouter>
          </DevLogProvider>
        </SSEProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
};

export default App;
